import React, { useEffect, useState, useRef, useMemo } from 'react';
import { drillsAPI, uploadAPI } from '../../lib/api';
import Layout from '../../components/Layout';
import HLSPlayer from '../../components/HLSPlayer';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, X, Trash2, Upload, Loader2, Video, Clock, Edit, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Folder, Target, Zap, Dumbbell, Brain, Eye, Apple, Snowflake, Play, PlayCircle, Search } from 'lucide-react';
import { toast } from 'sonner';

const FOLDERS = [
  { key: 'stickhandling', label: 'Stickhandling', icon: Zap,       color: '#2563EB' },
  { key: 'shooting',      label: 'Shooting',      icon: Target,    color: '#FF9100' },
  { key: 'off-ice',       label: 'Off-Ice',       icon: Dumbbell,  color: '#10B981' },
  { key: 'mental-tips',   label: 'Mental Tips',   icon: Brain,     color: '#A78BFA' },
  { key: 'video-review',  label: 'Video Review',  icon: Eye,       color: '#00E5FF' },
  { key: 'nutrition-tips',label: 'Nutrition Tips',icon: Apple,     color: '#F472B6' },
  { key: 'skating',       label: 'Skating',       icon: Snowflake, color: '#64748B' },
];
const CATEGORY_KEYS = FOLDERS.map(f => f.key);
const DIFFICULTIES = ['AA', 'AAA', 'Elite', 'All'];
const LEVELS = ['10U', '12U', '14U', '15+'];

export default function CoachDrills() {
  const [drills, setDrills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFolder, setActiveFolder] = useState(null); // null = folder grid view
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', coaching_cues: [''], category: 'shooting', difficulty: 'All', levels: ['10U', '12U', '14U', '15+'], duration_minutes: 15, video_filenames: [] });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [viewingDrill, setViewingDrill] = useState(null);
  const [search, setSearch] = useState('');
  const fileRef = useRef(null);

  useEffect(() => { loadDrills(); }, []);

  const loadDrills = async () => {
    try {
      const res = await drillsAPI.list();
      setDrills(res.data || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  // Count drills per folder
  const folderCounts = useMemo(() => {
    const counts = {};
    for (const f of FOLDERS) counts[f.key] = 0;
    counts._uncategorized = 0;
    for (const d of drills) {
      const k = (d.category || '').toLowerCase();
      if (counts[k] !== undefined) counts[k] += 1;
      else counts._uncategorized += 1;
    }
    return counts;
  }, [drills]);

  const folderDrills = useMemo(() => {
    if (!activeFolder) return [];
    const inFolder = drills.filter(d => (d.category || '').toLowerCase() === activeFolder);
    const q = search.trim().toLowerCase();
    if (!q) return inFolder;
    return inFolder.filter(d =>
      (d.title || '').toLowerCase().includes(q) ||
      (d.description || '').toLowerCase().includes(q) ||
      (d.difficulty || '').toLowerCase().includes(q) ||
      (d.level || '').toLowerCase().includes(q)
    );
  }, [drills, activeFolder, search]);

  const resetForm = () => {
    setForm({ title: '', description: '', coaching_cues: [''], category: activeFolder || 'shooting', difficulty: 'All', levels: ['10U', '12U', '14U', '15+'], duration_minutes: 15, video_filenames: [] });
    setEditingId(null);
    setShowForm(false);
  };

  const handleEdit = (drill) => {
    setForm({
      title: drill.title,
      description: drill.description,
      coaching_cues: drill.coaching_cues?.length ? drill.coaching_cues : [''],
      category: drill.category,
      difficulty: drill.difficulty,
      levels: (drill.levels && drill.levels.length) ? drill.levels : ['10U', '12U', '14U', '15+'],
      duration_minutes: drill.duration_minutes,
      video_filenames: drill.video_filenames && drill.video_filenames.length
        ? drill.video_filenames
        : (drill.video_filename ? [drill.video_filename] : []),
    });
    setEditingId(drill.id);
    setShowForm(true);
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setUploadProgress(0);

    const uploaded = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        const res = await uploadAPI.video(file, (progressEvent) => {
          // Aggregate progress across multiple files
          const filePct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          const overall = Math.round(((i * 100) + filePct) / files.length);
          setUploadProgress(overall);
        });
        uploaded.push(res.data.filename);
      } catch (err) {
        const detail = err.response?.data?.detail;
        toast.error(`Upload failed for "${file.name}": ${detail || err.message || 'Check your connection'}`);
      }
    }

    if (uploaded.length) {
      const next = [...form.video_filenames, ...uploaded];
      setForm(f => ({ ...f, video_filenames: next }));
      if (editingId) {
        try {
          await drillsAPI.update(editingId, { video_filenames: next });
          toast.success(`${uploaded.length} video${uploaded.length > 1 ? 's' : ''} saved`);
          loadDrills();
        } catch {
          toast.success(`${uploaded.length} video${uploaded.length > 1 ? 's' : ''} uploaded — click Save to attach`);
        }
      } else {
        toast.success(`${uploaded.length} video${uploaded.length > 1 ? 's' : ''} uploaded — fill in details and save`);
      }
    }

    setUploading(false);
    setUploadProgress(0);
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleRemoveVideo = async (idx) => {
    const next = form.video_filenames.filter((_, i) => i !== idx);
    setForm(f => ({ ...f, video_filenames: next }));
    if (editingId) {
      try {
        await drillsAPI.update(editingId, { video_filenames: next });
        toast.success('Video removed');
        loadDrills();
      } catch {
        toast.error('Failed to remove — try again');
      }
    }
  };

  const handleMoveVideo = async (idx, direction) => {
    const next = [...form.video_filenames];
    const newIdx = idx + direction;
    if (newIdx < 0 || newIdx >= next.length) return;
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    setForm(f => ({ ...f, video_filenames: next }));
    if (editingId) {
      try {
        await drillsAPI.update(editingId, { video_filenames: next });
        loadDrills();
      } catch {
        toast.error('Reorder failed');
      }
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, coaching_cues: form.coaching_cues.filter(c => c.trim()) };
      if (editingId) {
        await drillsAPI.update(editingId, payload);
        toast.success('Drill updated');
      } else {
        await drillsAPI.create(payload);
        toast.success('Drill created');
      }
      resetForm();
      loadDrills();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : 'Failed to save';
      toast.error(msg);
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this drill?')) return;
    try {
      await drillsAPI.delete(id);
      toast.success('Deleted');
      loadDrills();
    } catch { toast.error('Failed to delete'); }
  };

  const addCue = () => setForm(f => ({ ...f, coaching_cues: [...f.coaching_cues, ''] }));
  const updateCue = (i, v) => setForm(f => ({ ...f, coaching_cues: f.coaching_cues.map((c, j) => j === i ? v : c) }));
  const removeCue = (i) => setForm(f => ({ ...f, coaching_cues: f.coaching_cues.filter((_, j) => j !== i) }));

  const activeFolderMeta = FOLDERS.find(f => f.key === activeFolder);

  return (
    <Layout title="DRILLS" showBack>
      <div className="px-4 py-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          {activeFolder ? (
            <button
              onClick={() => { setActiveFolder(null); setSearch(''); resetForm(); }}
              className="flex items-center gap-2 text-[#64748B] hover:text-[#0A2242] text-sm font-bold"
              data-testid="back-to-folders-btn"
            >
              <ChevronLeft size={16} /> All Folders
            </button>
          ) : (
            <h2 className="font-['Barlow_Condensed'] text-xl font-bold tracking-tight" data-testid="drills-title">
              VIDEO LIBRARY
            </h2>
          )}
          {activeFolder && (
            <button
              onClick={() => { resetForm(); setForm(f => ({ ...f, category: activeFolder })); setShowForm(true); }}
              className="bg-[#2563EB] hover:bg-[#3B82F6] text-white font-bold uppercase tracking-wide rounded-lg px-4 py-2 text-xs transition-colors flex items-center gap-1"
              data-testid="add-drill-btn"
            >
              <Plus size={14} /> Add
            </button>
          )}
        </div>

        {/* FOLDER GRID VIEW */}
        {!activeFolder && !loading && (
          <div className="grid grid-cols-2 gap-3" data-testid="folders-grid">
            {FOLDERS.map((f, i) => {
              const Icon = f.icon;
              const count = folderCounts[f.key] || 0;
              return (
                <motion.button
                  key={f.key}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.04 }}
                  onClick={() => setActiveFolder(f.key)}
                  className="bg-white border border-[#E2E8F0] hover:border-[#2563EB]/40 rounded-xl p-4 text-left transition-all active:scale-[0.98] shadow-sm"
                  data-testid={`folder-${f.key}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{ background: `${f.color}15`, border: `1px solid ${f.color}40` }}
                    >
                      <Icon size={20} style={{ color: f.color }} />
                    </div>
                    <span
                      className="font-['Barlow_Condensed'] text-2xl font-black"
                      style={{ color: f.color }}
                    >{count}</span>
                  </div>
                  <h3 className="font-['Barlow_Condensed'] text-base font-bold tracking-tight leading-tight">{f.label}</h3>
                  <p className="text-[10px] text-[#64748B] uppercase tracking-widest mt-1">
                    {count === 1 ? '1 item' : `${count} items`}
                  </p>
                </motion.button>
              );
            })}
            {folderCounts._uncategorized > 0 && (
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: FOLDERS.length * 0.04 }}
                onClick={() => setActiveFolder('_uncategorized')}
                className="bg-white border border-dashed border-[#E2E8F0] hover:border-[#EF4444]/40 rounded-xl p-4 text-left transition-all active:scale-[0.98]"
                data-testid="folder-uncategorized"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#EF4444]/10 border border-[#EF4444]/30">
                    <Folder size={20} className="text-[#EF4444]" />
                  </div>
                  <span className="font-['Barlow_Condensed'] text-2xl font-black text-[#EF4444]">{folderCounts._uncategorized}</span>
                </div>
                <h3 className="font-['Barlow_Condensed'] text-base font-bold tracking-tight leading-tight">Uncategorized</h3>
                <p className="text-[10px] text-[#64748B] uppercase tracking-widest mt-1">Re-assign these</p>
              </motion.button>
            )}
          </div>
        )}

        {/* FOLDER DETAIL — Title */}
        {activeFolder && activeFolderMeta && (
          <div className="flex items-center gap-3 pb-2">
            <div
              className="w-11 h-11 rounded-xl flex items-center justify-center"
              style={{ background: `${activeFolderMeta.color}15`, border: `1px solid ${activeFolderMeta.color}40` }}
            >
              <activeFolderMeta.icon size={22} style={{ color: activeFolderMeta.color }} />
            </div>
            <div>
              <h2 className="font-['Barlow_Condensed'] text-xl font-bold tracking-tight" style={{ color: activeFolderMeta.color }}>
                {activeFolderMeta.label.toUpperCase()}
              </h2>
              <p className="text-[10px] text-[#64748B] uppercase tracking-widest">
                {folderDrills.length} {folderDrills.length === 1 ? 'item' : 'items'}{search && ' · filtered'}
              </p>
            </div>
          </div>
        )}

        {/* Search bar (inside folder) */}
        {activeFolder && (
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search drills in this folder..."
              className="w-full bg-white border border-[#E2E8F0] rounded-lg pl-9 pr-9 py-2.5 text-sm text-[#0A2242] focus:outline-none focus:ring-2 focus:ring-[#2563EB] placeholder:text-[#94A3B8]"
              data-testid="drill-search-input"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-[#64748B] hover:text-[#0A2242]"
                data-testid="drill-search-clear"
                aria-label="Clear search"
              >
                <X size={14} />
              </button>
            )}
          </div>
        )}

        {/* Form Modal */}
        <AnimatePresence>
          {showForm && activeFolder && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 20 }}
              className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-4"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-['Barlow_Condensed'] text-base font-bold">{editingId ? 'EDIT DRILL' : 'NEW DRILL'}</h3>
                <button onClick={resetForm} className="text-[#64748B] hover:text-[#0A2242]"><X size={18} /></button>
              </div>

              <input
                value={form.title}
                onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-4 py-3 text-[#0A2242] text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] placeholder:text-[#64748B]"
                placeholder="Drill title"
                data-testid="drill-title-input"
              />

              <textarea
                value={form.description}
                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-4 py-3 text-[#0A2242] text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] placeholder:text-[#64748B] resize-none h-20"
                placeholder="Description"
                data-testid="drill-desc-input"
              />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold mb-1 block">Folder</label>
                  <select
                    value={form.category}
                    onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                    className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-3 py-2.5 text-[#0A2242] text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                    data-testid="drill-category-select"
                  >
                    {FOLDERS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold mb-1 block">Duration (min)</label>
                  <input
                    type="number"
                    value={form.duration_minutes}
                    onChange={e => setForm(f => ({ ...f, duration_minutes: parseInt(e.target.value) || 0 }))}
                    className="w-full bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-3 py-2.5 text-[#0A2242] text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB]"
                    data-testid="drill-duration-input"
                  />
                </div>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold mb-1 block">Levels</label>
                <div className="flex flex-wrap gap-2" data-testid="drill-levels-group">
                  {LEVELS.map(lvl => {
                    const checked = form.levels.includes(lvl);
                    return (
                      <button
                        type="button"
                        key={lvl}
                        onClick={() => setForm(f => ({
                          ...f,
                          levels: f.levels.includes(lvl) ? f.levels.filter(x => x !== lvl) : [...f.levels, lvl],
                        }))}
                        className={`px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border transition-colors ${
                          checked
                            ? 'bg-[#2563EB] text-white border-[#2563EB]'
                            : 'bg-[#F8FAFC] text-[#64748B] border-[#E2E8F0]'
                        }`}
                        data-testid={`drill-level-${lvl}`}
                      >
                        {lvl}
                      </button>
                    );
                  })}
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, levels: f.levels.length === LEVELS.length ? [] : [...LEVELS] }))}
                    className="px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wider border border-dashed border-[#94A3B8] text-[#64748B]"
                    data-testid="drill-level-all"
                  >
                    {form.levels.length === LEVELS.length ? 'Clear' : 'All Levels'}
                  </button>
                </div>
                <p className="text-[9px] text-[#94A3B8] mt-1.5">Only players at the selected tiers will see this drill.</p>
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold mb-1 block">Videos</label>

                {/* List of attached videos */}
                {form.video_filenames.length > 0 && (
                  <div className="space-y-1.5 mb-2" data-testid="drill-video-list">
                    {form.video_filenames.map((fn, i) => (
                      <div
                        key={fn + i}
                        className="flex items-center gap-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-3 py-2"
                        data-testid={`drill-video-row-${i}`}
                      >
                        <Video size={14} className="text-[#2563EB] flex-shrink-0" />
                        <span className="text-xs text-[#0A2242] font-medium flex-1 truncate" title={fn}>
                          {i === 0 && <span className="text-[9px] uppercase tracking-widest text-[#FF9100] font-bold mr-1">Main</span>}
                          {fn}
                        </span>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => handleMoveVideo(i, -1)}
                            disabled={i === 0}
                            className="text-[#64748B] hover:text-[#2563EB] disabled:opacity-30"
                            title="Move up"
                            data-testid={`drill-video-up-${i}`}
                          >
                            <ChevronUp size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleMoveVideo(i, 1)}
                            disabled={i === form.video_filenames.length - 1}
                            className="text-[#64748B] hover:text-[#2563EB] disabled:opacity-30"
                            title="Move down"
                            data-testid={`drill-video-down-${i}`}
                          >
                            <ChevronDown size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleRemoveVideo(i)}
                            className="text-[#64748B] hover:text-red-500 ml-1"
                            title="Remove"
                            data-testid={`drill-video-remove-${i}`}
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="w-full border-2 border-dashed border-[#E2E8F0] hover:border-[#2563EB] rounded-lg p-3 text-center text-sm transition-colors flex items-center justify-center gap-2"
                  data-testid="drill-upload-btn"
                >
                  {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading {uploadProgress}%</> :
                   form.video_filenames.length > 0 ? <><Upload size={14} className="text-[#2563EB]" /> Add another video</> :
                   <><Upload size={14} className="text-[#64748B]" /> Upload video</>}
                </button>
                {uploading && (
                  <div className="mt-2 w-full bg-[#E2E8F0] rounded-full h-1.5">
                    <div className="bg-[#2563EB] h-1.5 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                  </div>
                )}
                <p className="text-[10px] text-[#94A3B8] mt-1.5 leading-snug">
                  Add multiple angles, progressions, or examples. The first video shows as the drill thumbnail. You can pick multiple files at once.
                </p>
                <input ref={fileRef} type="file" accept="video/*" multiple onChange={handleUpload} className="hidden" />
              </div>

              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold mb-2 block">Coaching Cues</label>
                {form.coaching_cues.map((cue, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <input
                      value={cue}
                      onChange={e => updateCue(i, e.target.value)}
                      className="flex-1 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg px-3 py-2 text-[#0A2242] text-sm focus:outline-none focus:ring-2 focus:ring-[#2563EB] placeholder:text-[#64748B]"
                      placeholder={`Cue ${i + 1}`}
                    />
                    {form.coaching_cues.length > 1 && (
                      <button onClick={() => removeCue(i)} className="text-[#64748B] hover:text-red-400"><X size={16} /></button>
                    )}
                  </div>
                ))}
                <button onClick={addCue} className="text-xs text-[#2563EB] hover:text-[#3B82F6] font-medium">+ Add cue</button>
              </div>

              <button
                onClick={handleSave}
                disabled={saving}
                className="w-full bg-[#2563EB] hover:bg-[#3B82F6] text-white font-bold uppercase tracking-wide rounded-lg px-8 py-4 transition-colors disabled:opacity-50"
                data-testid="drill-save-btn"
              >
                {saving ? 'Saving...' : editingId ? 'Update Drill' : 'Create Drill'}
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* FOLDER DETAIL — Drill List */}
        {activeFolder && !loading && (
          folderDrills.length === 0 ? (
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-8 text-center">
              <Folder size={32} className="mx-auto text-[#CBD5E1] mb-2" />
              {search ? (
                <p className="text-[#64748B] text-sm">No drills match "<span className="font-bold">{search}</span>".</p>
              ) : (
                <p className="text-[#64748B] text-sm">No items in this folder yet. Tap Add to create one.</p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {folderDrills.map((drill, i) => (
                <motion.div
                  key={drill.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.03 }}
                  className="bg-white border border-[#E2E8F0] rounded-xl p-4 flex items-center gap-3"
                  data-testid={`drill-item-${i}`}
                >
                  <button
                    onClick={() => setViewingDrill(drill)}
                    className="flex-1 flex items-center gap-3 min-w-0 text-left active:scale-[0.99]"
                    data-testid={`view-drill-${i}`}
                  >
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#2563EB]/10 border border-[#2563EB]/20">
                      {drill.video_filename ? (
                        <PlayCircle size={18} className="text-[#2563EB]" />
                      ) : (
                        <Folder size={18} className="text-[#64748B]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-['Barlow_Condensed'] text-sm font-bold truncate">{drill.title}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[9px] text-[#64748B]">{drill.levels && drill.levels.length === 4 ? 'All Levels' : (drill.levels || []).join(', ')}</span>
                        <span className="flex items-center gap-0.5 text-[9px] text-[#64748B]"><Clock size={9} />{drill.duration_minutes}m</span>
                        {drill.video_filename && <Video size={10} className="text-[#2563EB]" />}
                      </div>
                    </div>
                  </button>
                  <button onClick={() => handleEdit(drill)} className="p-2 text-[#64748B] hover:text-[#2563EB] transition-colors" data-testid={`edit-drill-${i}`}>
                    <Edit size={16} />
                  </button>
                  <button onClick={() => handleDelete(drill.id)} className="p-2 text-[#64748B] hover:text-red-400 transition-colors" data-testid={`delete-drill-${i}`}>
                    <Trash2 size={16} />
                  </button>
                </motion.div>
              ))}
            </div>
          )
        )}

        {loading && <div className="flex justify-center py-12"><div className="spinner" /></div>}

        {/* View Drill Modal */}
        <AnimatePresence>
          {viewingDrill && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[90] bg-black/85 flex items-center justify-center p-3 overflow-y-auto"
              onClick={() => setViewingDrill(null)}
              data-testid="view-drill-modal"
            >
              <motion.div
                initial={{ scale: 0.92, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white w-full max-w-lg rounded-2xl overflow-hidden shadow-2xl my-6"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-[#E2E8F0]">
                  <div className="flex-1 min-w-0">
                    <p className="text-[9px] uppercase tracking-widest text-[#64748B] font-bold">Drill Preview</p>
                    <h3 className="font-['Barlow_Condensed'] text-lg font-bold truncate">{viewingDrill.title}</h3>
                  </div>
                  <button onClick={() => setViewingDrill(null)} className="ml-2 text-[#64748B] hover:text-[#0A2242] p-1" data-testid="view-drill-close-btn">
                    <X size={20} />
                  </button>
                </div>

                {/* Video */}
                {viewingDrill.video_filename ? (
                  <HLSPlayer
                    videoFilename={viewingDrill.video_filename}
                    watermarkText="Coach Preview"
                    className="w-full aspect-video bg-black"
                    data-testid="view-drill-video"
                  />
                ) : (
                  <div className="w-full aspect-video bg-[#F1F5F9] flex flex-col items-center justify-center gap-2">
                    <Video size={32} className="text-[#CBD5E1]" />
                    <p className="text-[#64748B] text-xs">No video attached</p>
                  </div>
                )}

                {/* Meta + cues */}
                <div className="px-5 py-4 space-y-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full bg-[#2563EB]/10 text-[#2563EB]">
                      {viewingDrill.category}
                    </span>
                    <span className="text-[10px] text-[#64748B] uppercase tracking-widest font-bold px-2 py-1 rounded-full bg-[#F1F5F9]">
                      {viewingDrill.levels && viewingDrill.levels.length === 4 ? 'All Levels' : (viewingDrill.levels || []).join(', ')}
                    </span>
                    <span className="text-[10px] text-[#64748B] uppercase tracking-widest font-bold px-2 py-1 rounded-full bg-[#F1F5F9] flex items-center gap-1">
                      <Clock size={10} />{viewingDrill.duration_minutes}m
                    </span>
                  </div>

                  {viewingDrill.description && (
                    <p className="text-sm text-[#0A2242] leading-relaxed">{viewingDrill.description}</p>
                  )}

                  {viewingDrill.coaching_cues?.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-widest text-[#64748B] font-bold mb-2">Coaching Cues</p>
                      <ul className="space-y-1.5">
                        {viewingDrill.coaching_cues.filter(c => c && c.trim()).map((cue, idx) => (
                          <li key={idx} className="flex items-start gap-2">
                            <span className="w-5 h-5 rounded-full bg-[#2563EB]/10 text-[#2563EB] text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{idx + 1}</span>
                            <span className="text-xs text-[#0A2242] leading-snug">{cue}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={() => { handleEdit(viewingDrill); setViewingDrill(null); }}
                      className="flex-1 bg-[#2563EB] hover:bg-[#3B82F6] text-white font-bold uppercase tracking-wide rounded-lg px-4 py-3 text-xs transition-colors flex items-center justify-center gap-2"
                      data-testid="view-drill-edit-btn"
                    >
                      <Edit size={14} /> Edit
                    </button>
                    <button
                      onClick={() => setViewingDrill(null)}
                      className="flex-1 bg-white border border-[#E2E8F0] text-[#64748B] font-bold uppercase tracking-wide rounded-lg px-4 py-3 text-xs transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Layout>
  );
}
